import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { template } from "lodash";
import cn from "classnames";

import FolderIcon from "@rsuite/icons/Folder";
import PageIcon from "@rsuite/icons/Page";
import ArrowUpLineIcon from "@rsuite/icons/ArrowUpLine";
import ArrowDownLineIcon from "@rsuite/icons/ArrowDownLine";

import { CustomerType } from "store/customers/add-new-customer/add-new-customer-types";
import { AppDispatch, RootState } from "store/store";
import {
    setActiveTenantById,
    setIsTenantFetched,
} from "store/environments/tenants/tenants-actions";
import { setIsDeleteFetched } from "store/environments/tenants/tenant-delete/tenant-delete-actions";
import { getActiveEnvironmentMoniker } from "store/environments/environment-selectors";
import { getTenantCustomerType } from "store/environments/tenants/tenants-selectors";
import { getActiveTenantDeleteWorkflow } from "store/environments/tenants/tenant-delete/tenant-delete-selectors";
import { getAppSettings } from "store/app-settings/app-settings-selectors";
import { fetchEnvironments } from "store/environments/environment-thunks";
import { fetchAppSettings } from "store/app-settings/app-settings-thunks";
import { fetchTenants } from "store/environments/tenants/tenants-thunks";

import constants from "utils/constants";

import defaultItems, { addUrl, NavItem } from "./sidbar-helpers";

import css from "./Sidebar.module.css";

type UrlParams = {
    environmentId: string;
    tenantId: string;
};

const LeftMenu: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const envMoniker = useSelector((state: RootState) =>
        getActiveEnvironmentMoniker(state)
    );
    const tenantMoniker = useSelector(
        (state: RootState) => state.tenantsReducer.activeTenant.moniker
    );
    const activeTenant = useSelector(
        (state: RootState) => state.tenantsReducer.activeTenant
    );
    const customerType = useSelector((state: RootState) =>
        getTenantCustomerType(state)
    );
    const activeTenantDeleteWorkflow = useSelector((state: RootState) =>
        getActiveTenantDeleteWorkflow(state)
    );
    const appSettings = useSelector((state: RootState) =>
        getAppSettings(state)
    );
    const params = useParams<UrlParams>();

    const [deleting, setDeleting] = useState(false);
    const [extended, setExtended] = useState(true);
    const navItems = addUrl(defaultItems, params);

    const { environmentId, tenantId } = params;

    let erpType: string;
    switch (customerType) {
        case CustomerType.AX2012:
            erpType = t("tenantConfig.d365InformationPage.erpType.ax");
            break;
        case CustomerType.D365:
            erpType = t("tenantConfig.d365InformationPage.erpType.d365");
            break;
        default:
            erpType = t("tenantConfig.d365InformationPage.erpType.default");
    }

    useEffect(() => {
        setDeleting(
            !!(
                activeTenant &&
                activeTenantDeleteWorkflow &&
                activeTenant.versionInfo.id ===
                    activeTenantDeleteWorkflow.tenantId
            )
        );
    }, [activeTenant, activeTenantDeleteWorkflow]);

    useEffect(() => {
        if (!envMoniker && environmentId) {
            dispatch(fetchEnvironments(environmentId));
        }
    }, [envMoniker, environmentId, fetchEnvironments]);

    useEffect(() => {
        if (!appSettings) {
            dispatch(fetchAppSettings());

            return;
        }
    }, [appSettings, fetchAppSettings]);

    // TODO SK: review in future to check whether it is needed
    useEffect(() => {
        if (envMoniker && !tenantMoniker) {
            dispatch(fetchTenants(envMoniker, tenantId));
        }
    }, [envMoniker, tenantId, tenantMoniker]);

    useEffect(() => {
        if (tenantId && activeTenant.versionInfo.id !== tenantId) {
            dispatch(setIsDeleteFetched(false));
            dispatch(setIsTenantFetched(false));
            dispatch(setActiveTenantById(tenantId));
        }
    }, [activeTenant.versionInfo.id, dispatch, tenantId]);

    const filterDataRefresh = (item: NavItem): boolean => {
        switch (customerType) {
            case CustomerType.D365:
                return !(
                    item.path === constants.routePath.dataRefreshHistory &&
                    !appSettings.isDataRefreshEnabled
                );
            case CustomerType.AX2012:
                return !(item.path === constants.routePath.dataRefreshHistory);
            default:
                return true;
        }
    };

    const handleExtendClick = () => {
        setExtended(!extended);
    };

    const composeSubItems = (subItems: NavItem[]) => {
        if (!extended) {
            return null;
        }

        return (
            <ul className={css.menu}>
                {subItems.map((subItem: NavItem, num: number) => {
                    return (
                        <li
                            key={String(num)}
                            onClick={(event) => {
                                event.stopPropagation();
                            }}
                        >
                            <NavLink
                                className={({ isActive }) =>
                                    cn(
                                        isActive && css.selected,
                                        css.subItem,
                                        deleting && css.itemDisabled
                                    )
                                }
                                to={subItem.url ?? ""}
                                onClick={(event) => {
                                    if (deleting) {
                                        event.stopPropagation();
                                        event.preventDefault();
                                    }
                                }}
                            >
                                <PageIcon className={css.icon} />
                                <span className={css.itemName}>
                                    {template(t(subItem.name))({
                                        erpType: erpType,
                                    })}
                                </span>
                            </NavLink>
                        </li>
                    );
                })}
            </ul>
        );
    };

    return (
        <ul className={cn(css.menu)}>
            {navItems
                .filter((item: NavItem) => filterDataRefresh(item))
                .map((item, index: number) => {
                    return !item.subItems || item.subItems.length === 0 ? (
                        <li key={String(index)}>
                            <NavLink
                                className={({ isActive }) =>
                                    cn(
                                        isActive && css.selected,
                                        css.item,
                                        deleting && css.itemDisabled
                                    )
                                }
                                to={item.url ?? ""}
                                onClick={(event) => {
                                    if (deleting) {
                                        event.stopPropagation();
                                        event.preventDefault();
                                    }
                                }}
                            >
                                <FolderIcon className={css.icon} />
                                <span className={css.itemName}>
                                    {template(t(item.name))({
                                        erpType: erpType,
                                    })}
                                </span>
                            </NavLink>
                        </li>
                    ) : (
                        <li key={String(index)}>
                            <NavLink
                                to={item.url ?? ""}
                                className={({ isActive }) =>
                                    cn(
                                        isActive && !extended && css.selected,
                                        css.item,
                                        deleting && css.itemDisabled
                                    )
                                }
                                onClick={(event) => {
                                    if (deleting) {
                                        event.stopPropagation();
                                        event.preventDefault();
                                    }
                                }}
                            >
                                <FolderIcon />
                                <span className={css.itemName}>
                                    {template(t(item.name))({
                                        erpType: erpType,
                                    })}
                                </span>
                                {extended ? (
                                    <ArrowDownLineIcon
                                        onClick={handleExtendClick}
                                    />
                                ) : (
                                    <ArrowUpLineIcon
                                        onClick={handleExtendClick}
                                    />
                                )}
                            </NavLink>
                            {composeSubItems(item.subItems!)}
                        </li>
                    );
                })}
        </ul>
    );
};

export default LeftMenu;
